import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
    ModelKeyImage,
    ModelShowImage,
    ModelUSMeasureShow,
    ModelUSStressEchoAssociateImages,
    RequestModelClose,
    RequestModelDiscard,
    RequestModelExaminationStatus,
    RequestModelViewStudies,
    ViewerIncomingApi,
    ViewerOutgoingApi,
} from '../ebit-viewer-api';
import { DeepPascalCase } from '../../../utils/types';
import { ExtensaService } from '../../../connectivity/extensa.service';
import { convertPropertiesToPascalCase } from '../../../utils/interop';

type EstensaReviewApi = {
    [key in keyof ViewerOutgoingApi]: (arg: ViewerOutgoingApi[key]) => Promise<void>;
};

type MessageHandler<Msg extends keyof ViewerIncomingApi> = ViewerIncomingApi[Msg] extends (
    ...args: unknown[]
) => unknown
    ? (...args: DeepPascalCase<Parameters<ViewerIncomingApi[Msg]>>) => void
    : (msg: DeepPascalCase<ViewerIncomingApi[Msg]>) => void;

@Injectable({
    providedIn: 'root',
})
export class EstensaReviewApiService implements EstensaReviewApi {
    constructor(
        private http: HttpClient,
        private extensaService: ExtensaService,
        private zone: NgZone,
    ) {}

    on<Msg extends keyof ViewerIncomingApi>(hub: SignalR.Hub.Proxy, message: Msg, handler: MessageHandler<Msg>) {
        hub.on(message, (...args) => this.zone.run(() => (handler as any)(...args)));
        return this;
    }

    async close(arg: RequestModelClose) {
        return firstValueFrom(
            this.http.post<void>(`${this.extensaService.getBaseUrl()}/Host/Close`, convertPropertiesToPascalCase(arg)),
        );
    }

    async discard(arg: RequestModelDiscard) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/Discard`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async examinationStatus(arg: RequestModelExaminationStatus) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/ExaminationStatus`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async viewImages(arg: RequestModelViewStudies) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/ViewImages`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async showImage(arg: ModelShowImage) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/ShowImage`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }

    async deleteKeyImage(arg: ModelKeyImage) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/DeleteKeyImage`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }
    async usMeasureShow(arg: ModelUSMeasureShow) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USMeasureShow`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }
    async usStressEchoAssociateImages(arg: ModelUSStressEchoAssociateImages) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USStressEchoAssociateImages`,
                convertPropertiesToPascalCase(arg),
                await this.extensaService.getAuthOptions(),
            ),
        );
    }
    async usStressEchoSelectStage(arg: string) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USStressEchoSelectStage`,
                `"${arg}"`,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }
    async usStressEchoSelectView(arg: string) {
        return firstValueFrom(
            this.http.post<void>(
                `${this.extensaService.getBaseUrl()}/Review/USStressEchoSelectView`,
                `"${arg}"`,
                await this.extensaService.getAuthOptions(),
            ),
        );
    }
}
